import { BATCH_SKIP_REASONS } from "./batch-preflight";
import { BatchResultSkip } from "./batch-result-summary";

export interface IdentityQueueCandidate {
  id: number;
}

export interface IdentityQueueEntry<TItem extends IdentityQueueCandidate> {
  item: TItem;
  installmentsCount?: number;
}

export interface IdentitySplit<TItem extends IdentityQueueCandidate> {
  queue: IdentityQueueEntry<TItem>[];
  remaining: BatchResultSkip[];
}

const isIdentitySkip = (skip: BatchResultSkip): boolean =>
  skip.reason === BATCH_SKIP_REASONS.OPERATOR_UNRESOLVED;

/**
 * D12: o lote não decide identidade, ele entrega o que não conseguiu. A fila
 * reenvia cada item pelo caminho individual, e é a API que volta a dizer se cabe
 * informar o CPF — por isso aqui não se lê falha nem elegibilidade, só o motivo
 * do lote (regra permanente 5).
 */
export const splitIdentitySkips = <TItem extends IdentityQueueCandidate>(
  skipped: readonly BatchResultSkip[],
  rows: readonly TItem[],
  installmentsById: Readonly<Record<number, number | undefined>>,
): IdentitySplit<TItem> => {
  const rowById: Map<number, TItem> = new Map(
    rows.map((row: TItem): [number, TItem] => [row.id, row]),
  );

  const queue: IdentityQueueEntry<TItem>[] = [];
  const remaining: BatchResultSkip[] = [];

  (skipped ?? []).forEach((skip: BatchResultSkip): void => {
    if (!isIdentitySkip(skip)) {
      remaining.push(skip);
      return;
    }

    const item: TItem | undefined =
      skip.id === null ? undefined : rowById.get(skip.id);

    if (!item) {
      remaining.push(skip);
      return;
    }

    queue.push({ item, installmentsCount: installmentsById[item.id] });
  });

  return { queue, remaining };
};

export const installmentsByIdOf = (
  decisions: readonly { id: number; installments_count?: number }[],
): Record<number, number | undefined> =>
  Object.fromEntries(
    decisions.map(
      (decision: {
        id: number;
        installments_count?: number;
      }): [number, number | undefined] => [
        decision.id,
        decision.installments_count,
      ],
    ),
  );
